import React, { useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Loader2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

// CSV Template
const CSV_TEMPLATE = `level,meta_campaign_id,meta_adset_id,meta_ad_id,meta_form_id,period_start,period_end,ad_spend
campaign,camp_001,,,2024-01-01,2024-01-31,5000.00
adset,camp_001,adset_001,,,2024-01-01,2024-01-31,2500.00
ad,camp_001,adset_001,ad_001,,2024-01-01,2024-01-31,1500.00
form,camp_001,adset_001,ad_001,form_001,2024-01-01,2024-01-31,800.00`;

// Parse CSV text to array of objects
function parseCSV(text) {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    const row = {};

    headers.forEach((header, idx) => {
      row[header] = values[idx] || "";
    });

    rows.push(row);
  }

  return rows;
}

// Download template
function downloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "ad_spend_template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Validation status badge
function ValidationBadge({ isValid, errors, warnings }) {
  if (isValid && warnings.length === 0) {
    return (
      <Badge className="bg-emerald-500/20 text-emerald-300">
        <CheckCircle className="h-3 w-3 mr-1" />
        Valid
      </Badge>
    );
  }

  if (!isValid) {
    return (
      <Badge className="bg-red-500/20 text-red-300">
        <XCircle className="h-3 w-3 mr-1" />
        Invalid
      </Badge>
    );
  }

  return (
    <Badge className="bg-amber-500/20 text-amber-300">
      <AlertTriangle className="h-3 w-3 mr-1" />
      Warning
    </Badge>
  );
}

// Main Component
export default function AdSpendCSVImport({ onImportComplete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState("upload"); // upload | preview | importing | done
  const [loading, setLoading] = useState(false);
  const [rawRows, setRawRows] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [ignoreOverlaps, setIgnoreOverlaps] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = useCallback(() => {
    setStep("upload");
    setRawRows([]);
    setPreviewData(null);
    setIgnoreOverlaps(false);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error("Please select a CSV file");
      return;
    }

    const text = await file.text();
    const rows = parseCSV(text);

    if (rows.length === 0) {
      toast.error("CSV file is empty or invalid");
      return;
    }

    setRawRows(rows);
    await validateCSV(rows);
  };

  const validateCSV = async (rows) => {
    setLoading(true);
    try {
      const res = await api.post("/ad-spend-entries/csv/preview", rows);
      setPreviewData(res.data);
      setStep("preview");
    } catch (err) {
      console.error("Validation error:", err);
      toast.error(err.response?.data?.detail || "Failed to validate CSV");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!previewData || previewData.valid_rows === 0) {
      toast.error("No valid rows to import");
      return;
    }

    setStep("importing");
    setLoading(true);

    try {
      // Only send valid rows
      const validRows = rawRows.filter((_, idx) => {
        const preview = previewData.rows[idx];
        return preview?.is_valid;
      });

      const res = await api.post("/ad-spend-entries/csv/import", {
        rows: validRows,
        ignore_overlaps: ignoreOverlaps,
      });

      setImportResult(res.data);
      setStep("done");
      toast.success(`Successfully imported ${res.data.imported} entries`);

      if (onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      console.error("Import error:", err);
      toast.error(err.response?.data?.detail || "Failed to import CSV");
      setStep("preview");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(resetState, 300);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="bg-white/5 text-slate-100 border-white/10 hover:bg-white/10"
          data-testid="csv-import-button"
        >
          <Upload className="h-4 w-4 mr-2" />
          Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col bg-slate-950 text-slate-100 ring-1 ring-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <FileText className="h-5 w-5 text-indigo-400" />
            Import Ad Spend Data
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Upload a CSV file with Meta ad spend data. Required columns: level,
            meta_campaign_id, period_start, period_end, ad_spend
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {/* Step: Upload */}
          {step === "upload" && (
            <div className="space-y-4 py-4">
              <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
                <Upload className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="text-slate-300 mb-4">
                  Drag and drop a CSV file, or click to select
                </p>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="max-w-xs mx-auto"
                  data-testid="csv-file-input"
                />
              </div>

              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  className="text-slate-300"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>

              <Alert className="bg-slate-800/50 border-slate-700">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>CSV Format Requirements</AlertTitle>
                <AlertDescription className="text-sm text-slate-400">
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>
                      <strong>level:</strong> campaign, adset, ad, or form
                    </li>
                    <li>
                      <strong>meta_campaign_id:</strong> Required for all levels
                    </li>
                    <li>
                      <strong>meta_adset_id:</strong> Required for adset, ad,
                      form levels
                    </li>
                    <li>
                      <strong>meta_ad_id:</strong> Required for ad, form levels
                    </li>
                    <li>
                      <strong>meta_form_id:</strong> Required for form level
                    </li>
                    <li>
                      <strong>period_start/period_end:</strong> YYYY-MM-DD format
                    </li>
                    <li>
                      <strong>ad_spend:</strong> Numeric value (e.g., 1234.56)
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Step: Preview */}
          {step === "preview" && previewData && (
            <div className="space-y-4 py-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-slate-100">
                      {previewData.total_rows}
                    </p>
                    <p className="text-xs text-slate-400">Total Rows</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-900/30 border-emerald-700">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400">
                      {previewData.valid_rows}
                    </p>
                    <p className="text-xs text-slate-400">Valid</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-900/30 border-red-700">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-red-400">
                      {previewData.invalid_rows}
                    </p>
                    <p className="text-xs text-slate-400">Invalid</p>
                  </CardContent>
                </Card>
              </div>

              {/* Overlap Warnings */}
              {previewData.has_overlaps && (
                <Alert className="bg-amber-900/30 border-amber-700">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertTitle className="text-amber-300">
                    Overlapping Date Ranges Detected
                  </AlertTitle>
                  <AlertDescription className="text-sm text-amber-200/80">
                    <ul className="list-disc list-inside mt-2">
                      {previewData.overlap_warnings.slice(0, 5).map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                      {previewData.overlap_warnings.length > 5 && (
                        <li>
                          ... and {previewData.overlap_warnings.length - 5} more
                        </li>
                      )}
                    </ul>
                    <div className="flex items-center gap-2 mt-3">
                      <Checkbox
                        id="ignore-overlaps"
                        checked={ignoreOverlaps}
                        onCheckedChange={setIgnoreOverlaps}
                      />
                      <label
                        htmlFor="ignore-overlaps"
                        className="text-sm cursor-pointer"
                      >
                        Import anyway (overlapping entries will be summed)
                      </label>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Preview Table */}
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <div className="max-h-[300px] overflow-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-slate-800">
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300 w-20">Row</TableHead>
                        <TableHead className="text-slate-300">Status</TableHead>
                        <TableHead className="text-slate-300">Level</TableHead>
                        <TableHead className="text-slate-300">Campaign ID</TableHead>
                        <TableHead className="text-slate-300">Period</TableHead>
                        <TableHead className="text-slate-300 text-right">
                          Spend
                        </TableHead>
                        <TableHead className="text-slate-300">Issues</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.rows.map((row) => (
                        <TableRow
                          key={row.row_num}
                          className={`border-slate-700 ${
                            !row.is_valid ? "bg-red-900/20" : ""
                          }`}
                        >
                          <TableCell className="font-mono text-slate-400">
                            {row.row_num}
                          </TableCell>
                          <TableCell>
                            <ValidationBadge
                              isValid={row.is_valid}
                              errors={row.errors}
                              warnings={row.warnings}
                            />
                          </TableCell>
                          <TableCell className="text-slate-100 capitalize">
                            {row.level}
                          </TableCell>
                          <TableCell className="text-slate-100 font-mono text-xs">
                            {row.meta_campaign_id?.slice(0, 12)}...
                          </TableCell>
                          <TableCell className="text-slate-300 text-xs">
                            {row.period_start} → {row.period_end}
                          </TableCell>
                          <TableCell className="text-right font-mono text-emerald-400">
                            ${row.ad_spend?.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {row.errors.length > 0 && (
                              <ul className="text-xs text-red-400">
                                {row.errors.map((e, i) => (
                                  <li key={i}>• {e}</li>
                                ))}
                              </ul>
                            )}
                            {row.warnings.length > 0 && (
                              <ul className="text-xs text-amber-400">
                                {row.warnings.map((w, i) => (
                                  <li key={i}>• {w}</li>
                                ))}
                              </ul>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* Step: Importing */}
          {step === "importing" && (
            <div className="py-12 text-center">
              <Loader2 className="h-12 w-12 mx-auto mb-4 text-indigo-400 animate-spin" />
              <p className="text-slate-300">Importing {previewData?.valid_rows} entries...</p>
            </div>
          )}

          {/* Step: Done */}
          {step === "done" && importResult && (
            <div className="py-8 text-center space-y-4">
              <CheckCircle className="h-16 w-16 mx-auto text-emerald-400" />
              <div>
                <p className="text-2xl font-bold text-slate-100">Import Complete</p>
                <p className="text-slate-400 mt-2">
                  Successfully imported {importResult.imported} entries
                </p>
                {importResult.skipped > 0 && (
                  <p className="text-amber-400 text-sm">
                    {importResult.skipped} rows skipped due to errors
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Import Log ID: {importResult.import_log_id}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-white/10 pt-4">
          {step === "upload" && (
            <Button variant="outline" onClick={handleClose} className="border-slate-600 text-slate-200 hover:bg-slate-800">
              Cancel
            </Button>
          )}

          {step === "preview" && (
            <>
              <Button variant="outline" onClick={resetState} className="border-slate-600 text-slate-200 hover:bg-slate-800">
                Back
              </Button>
              <Button
                onClick={handleImport}
                disabled={previewData?.valid_rows === 0 || loading}
                className="bg-indigo-500/90 text-white hover:bg-indigo-500"
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Import {previewData?.valid_rows} Valid Rows
              </Button>
            </>
          )}

          {step === "done" && (
            <Button onClick={handleClose} className="bg-indigo-500/90 text-white hover:bg-indigo-500">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
